import type { MaplibreIconProps } from "@/features/map/layers/maplibre/PoiLayer/poi-images";
import {
  DEFAULT_POI_ICON_FILENAME,
  FIND_MY_CAR_LOCATION_ID,
  type Poi,
  type PoiCategories,
} from "@/libs/models";
import { handleRTLText, isStringRTL } from "../utils";

export const MAX_TEXT_LEN = 40;
export const CIRCLE_ICON_Y_OFFSET = 20;
export const ORIGINAL_ICON_IMAGE_SIZE = 96;
export const DEFAULT_POI_ICON_SIZE = 30;
export const ICON_SIZE = 96;
export const POI_ICON_SIZE = 15;
export const POI_ICON_SIZE_SCALE = 2;
export const FAV_BADGE_SIZE = 1;
export const CURRENT_POI_COLOR = [152, 251, 152];
export const SQUARE_ICON_WIDTH = 100;
export const SQUARE_ICON_HEIGHT = 120;
export const TRIANGLE_TIP_HEIGHT = 120;
export const TRIANGLE_TIP_WIDTH = 120;
export const TRIANGLE_TIP_MINIMIZE_FACTOR = 0.25;
export const TRIANGLE_TIP_MAX_HEIGHT = 10;
export const TRIANGLE_TIP_MIN_HEIGHT = -10;
export const HIGHLIGHTED_POI_ICON_BORDER_WIDTH = 10;
export const HIGHLIGHTED_POI_GAINANCE = 1.3;

export const commonIconProps = {
  anchorY: ORIGINAL_ICON_IMAGE_SIZE,
  height: ORIGINAL_ICON_IMAGE_SIZE,
  width: ORIGINAL_ICON_IMAGE_SIZE,
};

export const getPoiLayerText = (d: Poi, debug: boolean) => {
  let text = d.getTranslatedProperty("name");

  if (text == null) return "";

  if (debug) {
    if (d.isTopLevel()) text += " top ";
    const priority = d.getCustomFieldValue("priority");
    if (priority) text += " p: " + priority;
    const minZoom = d.getCustomFieldValue("min_zoom");
    if (minZoom) text += " mz: " + minZoom;
    const maxZoom = d.getCustomFieldValue("max_zoom");
    if (maxZoom) text += " MZ: " + maxZoom;
  }

  // If not RTL return the text directly.
  if (!isStringRTL(text)) {
    // Check for overflow
    return text.length < MAX_TEXT_LEN
      ? text
      : text.substring(0, MAX_TEXT_LEN) + " ...";
  }

  return handleRTLText(text);
};

/*
 * Show label if the poi category does not have a hide cf set to on_map_text and poi
 * does not have cf "hide" set to on_map_text.
 * Show label if the poi category does not have a hide cf set to on_map_icon and poi
 * does not have cf "hide" set to on_map_icon.
 * When poi is selected show label if both on_map_text and on_map_icon are present on hide cf
 * Return - showIcon          -> result of searching for on_map_text on "hide" cf
 *        - showLabel         -> result of searching for on_map_icon on "hide" cf
 *        - hideIconAndLabel  -> true if both on_map_text and on_map_icon are on poi or
 *                                poi categories "hide" cf
 */
export const getPoiVisibility = (
  d: Poi,
  poiCategories: PoiCategories | null,
) => {
  const categoryHideValues = poiCategories
    ?.findCategoryById(d.categoryId)
    ?.getCustomFieldValue("hide")
    ?.split(",")
    .map((value) => value.trim());
  const poiHideValues = d
    .getCustomFieldValue("hide")
    ?.split(",")
    .map((value) => value.trim());

  const showLabel =
    !categoryHideValues?.includes("on_map_text") &&
    !poiHideValues?.includes("on_map_text");

  const showIcon =
    !categoryHideValues?.includes("on_map_icon") &&
    !poiHideValues?.includes("on_map_icon");

  const hideIconAndLabel =
    (categoryHideValues?.includes("on_map_text") &&
      categoryHideValues?.includes("on_map_icon")) ||
    (poiHideValues?.includes("on_map_text") &&
      poiHideValues?.includes("on_map_icon"));

  return { hideIconAndLabel: !!hideIconAndLabel, showIcon, showLabel };
};

export const getBorderIcon = (d: Poi, poiCategories?: PoiCategories | null) => {
  return {
    ...getSelectedIcon(d, poiCategories),
    mask: true,
  };
};

export const getIcon = (
  d: Poi,
  poiCategories?: PoiCategories | null,
): MaplibreIconProps => {
  if (d.categoryId === FIND_MY_CAR_LOCATION_ID) {
    return {
      ...commonIconProps,
      color: "#111111",
      id: d.id.toString(),
      url: "/images/car.svg",
    };
  } else {
    const category = poiCategories?.findCategoryById(d.categoryId);
    return {
      ...commonIconProps,
      color: category?.color,
      id: d.id.toString(),
      url: d.icon || category?.iconUrl || "/images/minimal-dot.png",
    };
  }
};

export const getSelectedIcon = (
  d: Poi,
  poiCategories?: PoiCategories | null,
): MaplibreIconProps => {
  const category = poiCategories?.findCategoryById(d.categoryId);
  const selectedIconUrl = d.selectedIcon || category?.selectedIconUrl;
  const useSelectedIcon =
    selectedIconUrl && !selectedIconUrl.includes(DEFAULT_POI_ICON_FILENAME);

  return {
    ...commonIconProps,
    color: category?.color,
    id: d.id.toString(),
    url: useSelectedIcon
      ? selectedIconUrl
      : d.icon || category?.iconUrl || "/images/minimal-dot.png",
  };
};
